import { Fragment } from 'react'
import { LOW_CONFIDENCE, foldNumbers, queryTerms, type Passage } from './model'

/**
 * What the paper printed, with two marks over it and no third.
 *
 * A search match takes the accent, which is one of its four reserved jobs. A
 * token the recognition doubted takes a dotted magenta underline and nothing
 * else: the letterforms are not altered, so a researcher reading for a
 * quotation sees exactly what the recognition produced. Below 85% the whole
 * passage takes the magenta tint and a 2px left rule, and a review item is
 * queued. Nothing is hidden and nothing is silently repaired.
 */

function stems(query: string): string[] {
  return queryTerms(query).map((term) => (term.length > 5 ? term.slice(0, term.length - 2) : term))
}

function bare(token: string): string {
  return foldNumbers(token.toLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
}

function Marked({
  text,
  matchStems,
  doubted,
}: {
  text: string
  matchStems: string[]
  doubted: Set<string>
}) {
  const tokens = text.split(/(\s+)/)
  return (
    <>
      {tokens.map((token, index) => {
        if (!token.trim()) return <Fragment key={index}>{token}</Fragment>
        const word = bare(token)
        const isMatch = !!word && matchStems.some((stem) => word.includes(stem))
        const isDoubted = !!word && doubted.has(word)
        if (isMatch && isDoubted) {
          return (
            <mark key={index}>
              <span className="doubt-token">{token}</span>
            </mark>
          )
        }
        if (isMatch) return <mark key={index}>{token}</mark>
        if (isDoubted) {
          return (
            <span key={index} className="doubt-token">
              {token}
            </span>
          )
        }
        return <Fragment key={index}>{token}</Fragment>
      })}
    </>
  )
}

export function PassageText({
  passages,
  query = '',
  size = 16,
}: {
  passages: Passage[]
  query?: string
  size?: number
}) {
  const matchStems = stems(query)
  return (
    <div style={{ fontSize: size, lineHeight: 1.62 }}>
      {passages.map((passage) => {
        const doubted = new Set(
          (passage.words ?? [])
            .filter((word) => word[5] < LOW_CONFIDENCE && word[4])
            .map((word) => bare(word[4])),
        )
        const low =
          passage.confidence !== null &&
          passage.confidence !== undefined &&
          passage.confidence < LOW_CONFIDENCE
        return (
          <p
            key={passage.id}
            className={low ? 'doubt-block mb-3 rounded-r-[--radius-md]' : 'mb-3'}
          >
            <Marked text={passage.text} matchStems={matchStems} doubted={doubted} />
          </p>
        )
      })}
    </div>
  )
}

/**
 * What the doubt marks mean, said once under the passage they mark. Never
 * fabricate: where confidence is low the interface says so in words as well as
 * in colour.
 */
export function DoubtNote({
  passages,
  reviewQueued,
}: {
  passages: Passage[]
  reviewQueued: boolean
}) {
  const low = passages.filter(
    (passage) =>
      passage.confidence !== null &&
      passage.confidence !== undefined &&
      passage.confidence < LOW_CONFIDENCE,
  )
  if (!low.length) return null
  const flagged = low.reduce(
    (total, passage) =>
      total + (passage.words ?? []).filter((word) => word[5] < LOW_CONFIDENCE).length,
    0,
  )
  const worst = Math.min(...low.map((passage) => passage.confidence ?? 1))
  return (
    <p className="mt-1 text-[14.5px] leading-normal text-ink2">
      {flagged > 0
        ? `${flagged} token${flagged === 1 ? '' : 's'} read below 85%, and `
        : `${low.length} passage${low.length === 1 ? '' : 's'} read below 85%, so `}
      the block score of {(worst * 100).toFixed(1)}% is set in magenta rather than green
      {reviewQueued ? ' and a review item is queued' : ''}. Nothing is hidden and nothing is
      silently repaired. The researcher decides whether this passage will carry their argument.
    </p>
  )
}
